/**
 * Environment Configuration
 *
 * Centralized configuration for all environment variables.
 * This provides type-safe access to environment variables and ensures
 * consistent usage across the application.
 */

/**
 * Application environment type
 */
export type AppEnvironment = "development" | "production" | "staging";

/**
 * Application configuration interface
 */
export interface AppConfig {
  api: {
    baseUrl: string;
    backendUrl: string;
    anomalyUrl: string;
    timeout: number;
  };
  websocket: {
    url: string;
    enabled: boolean;
    reconnectInterval: number;
    maxReconnectAttempts: number;
  };
  app: {
    version: string;
    environment: AppEnvironment;
    debug: boolean;
  };
  features: {
    enableWebSockets: boolean;
    enableDebugTools: boolean;
  };
  isProduction: () => boolean;
  isDevelopment: () => boolean;
  isDebug: () => boolean;
}

/**
 * Get environment variable with fallback
 * @param key - Environment variable key
 * @param fallback - Fallback value if env var is not set
 * @returns Environment variable value or fallback
 */
const getEnvVar = (key: string, fallback: string = ""): string => {
  return process.env[key] || fallback;
};

/**
 * Get boolean environment variable
 * @param key - Environment variable key
 * @param fallback - Fallback value
 * @returns Boolean value
 */
const getBoolEnvVar = (key: string, fallback: boolean = false): boolean => {
  const value = process.env[key];
  if (value === undefined || value === null) return fallback;
  return value === "true" || value === "1";
};

/**
 * Parse environment value to AppEnvironment type
 * @param value - Environment value
 * @returns Valid AppEnvironment type
 */
const parseEnvironment = (value: string): AppEnvironment => {
  if (value === "production" || value === "staging") {
    return value;
  }
  return "development";
};

/**
 * Application configuration object
 */
export const config: AppConfig = {
  // API Configuration
  api: {
    baseUrl: getEnvVar("REACT_APP_API_URL", ""),
    backendUrl: getEnvVar("REACT_APP_BACKEND_URL", ""),
    anomalyUrl: getEnvVar(
      "REACT_APP_ANOMALY_API_URL",
      "http://k8s-default-anomalym-963a76e5f0-2911352f5111268f.elb.us-east-2.amazonaws.com"
    ),
    timeout: 30000, // 30 seconds
  },

  // WebSocket Configuration (disabled by default)
  websocket: {
    url: getEnvVar("REACT_APP_WS_URL", ""),
    enabled: getBoolEnvVar("REACT_APP_ENABLE_WEBSOCKETS", false), // Disabled by default
    reconnectInterval: 5000, // 5 seconds
    maxReconnectAttempts: 5,
  },

  // Application Configuration
  app: {
    version: getEnvVar("REACT_APP_VERSION", "0.1.0"),
    environment: parseEnvironment(getEnvVar("REACT_APP_ENV", "development")),
    debug: getBoolEnvVar("REACT_APP_DEBUG", false),
  },

  // Feature Flags
  features: {
    enableWebSockets: getBoolEnvVar("REACT_APP_ENABLE_WEBSOCKETS", false), // Disabled by default
    enableDebugTools: getBoolEnvVar("REACT_APP_ENABLE_DEBUG_TOOLS", false),
  },

  // Helper methods
  isProduction: (): boolean => config.app.environment === "production",
  isDevelopment: (): boolean => config.app.environment === "development",
  isDebug: (): boolean => config.app.debug,
};

/**
 * Validate required environment variables
 * @throws {Error} If required variables are missing
 */
export const validateConfig = (): void => {
  const required = ["REACT_APP_API_URL", "REACT_APP_BACKEND_URL"];

  const missing = required.filter((key) => !process.env[key]);

  if (missing.length > 0) {
    console.warn(
      `Warning: Missing environment variables: ${missing.join(", ")}\n` +
        `Using default values. Check your .env file.`
    );
  }

  // Log configuration in development mode
  if (config.isDebug()) {
    console.log("📋 Application Configuration:", {
      environment: config.app.environment,
      apiUrl: config.api.baseUrl,
      backendUrl: config.api.backendUrl,
      anomalyUrl: config.api.anomalyUrl,
      wsUrl: config.websocket.url,
      debug: config.app.debug,
      version: config.app.version,
    });
  }
};

// Validate on module load
validateConfig();

export default config;
